
import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useUser } from './UserContext';
import {
  getCart,
  addToCart,
  updateCartItemQuantity,
  removeCartItem,
  clearCart,
  migrateGuestCart
} from '../firebase/cart-functions';
import { formatCurrencyDisplay } from '../utils/currencyUtils';
import { useLanguage } from './LanguageContext';
import { useStoreSettings } from '../hooks/useStoreSettings';
import { DEFAULT_STORE_ID } from '../firebase/firebase';

/**
 * Generate display variant text for cart items
 * @param {object} product - Product data
 * @param {object} variant - Variant data
 * @returns {string} Display variant text for English (can be localized later)
 */
const generateDisplayVariant = (product, variant) => {
  if (!variant) return null;

  const variantParts = [];

  // If product has options, use them to generate display text
  if (product.options && Array.isArray(product.options)) {
    product.options.forEach(option => {
      // Get option key (always use English for lookup)
      const optionKey = typeof option.name === 'string'
        ? option.name.toLowerCase()
        : (option.name?.en || Object.values(option.name || {})[0] || '').toLowerCase();

      const value = variant[optionKey];
      if (value) {
        // Get option display name (use English for now)
        const optionName = typeof option.name === 'string'
          ? option.name
          : (option.name?.en || Object.values(option.name || {})[0] || '');

        let displayValue = value;

        // Handle multilingual values - use English for cart storage
        try {
          const valueData = JSON.parse(value);
          if (typeof valueData === 'object' && valueData !== null) {
            displayValue = valueData.en || Object.values(valueData)[0] || value;
          }
        } catch {
          displayValue = value;
        }

        if (optionName && displayValue) {
          variantParts.push(`${optionName}: ${displayValue}`);
        }
      }
    });
  } else {
    // Fallback: use common variant properties
    const commonProps = ['color', 'size', 'material', 'style', 'type'];
    commonProps.forEach(prop => {
      if (variant[prop]) {
        const propName = prop.charAt(0).toUpperCase() + prop.slice(1);
        let displayValue = variant[prop];

        // Handle multilingual values
        try {
          const valueData = JSON.parse(displayValue);
          if (typeof valueData === 'object' && valueData !== null) {
            displayValue = valueData.en || Object.values(valueData)[0] || displayValue;
          }
        } catch {
          // Keep original value if parsing fails
        }

        if (displayValue) {
          variantParts.push(`${propName}: ${displayValue}`);
        }
      }
    });
  }

  return variantParts.length > 0 ? variantParts.join(' • ') : null;
};

const CartContext = createContext({});

export const CartProvider = ({ children }) => {
  // Get language and store settings for currency formatting
  const { language } = useLanguage();
  const { storeSettings } = useStoreSettings();

  // State declarations first
  const { currentUser } = useUser();
  const [cart, setCart] = useState({ items: [], subtotal: 0 });
  const [loading, setLoading] = useState(true);
  const [cartOpen, setCartOpen] = useState(false);
  const [cartVisible, setCartVisible] = useState(false);

  // Add a state to track if we have performed the initial load
  const [isInitialized, setIsInitialized] = useState(false);

  // Functions that depend on state
  const openCart = () => {
    setCartVisible(true);
    setTimeout(() => setCartOpen(true), 10);
  };

  const closeCart = () => {
    setCartOpen(false);
    setTimeout(() => setCartVisible(false), 400);
  };

  // Filter cart items to only include those from the current store
  const filterCartByStore = (cartData) => {
    if (!cartData || !cartData.items) return { items: [], subtotal: 0 };

    // Filter items by store ID
    const filteredItems = cartData.items.filter(item => item.storeId === DEFAULT_STORE_ID);

    // Recalculate subtotal based on filtered items
    const filteredSubtotal = filteredItems.reduce((total, item) => {
      return total + (item.price * item.quantity);
    }, 0);

    return {
      items: filteredItems,
      subtotal: filteredSubtotal
    };
  };
  // Add this function to fix any inconsistent store IDs in localStorage
  const fixCartStoreIds = useCallback(() => {
    try {
      const cartString = localStorage.getItem('guestCart');
      if (cartString) {
        const cart = JSON.parse(cartString);
        let needsUpdate = false;

        // Update main storeId
        if (cart.storeId !== DEFAULT_STORE_ID) {
          cart.storeId = DEFAULT_STORE_ID;
          needsUpdate = true;
        }

        // Update storeId in each item
        if (cart.items && Array.isArray(cart.items)) {
          cart.items.forEach(item => {
            if (item.storeId !== DEFAULT_STORE_ID) {
              item.storeId = DEFAULT_STORE_ID;
              needsUpdate = true;
            }
          });
        }

        if (needsUpdate) {
          localStorage.setItem('guestCart', JSON.stringify(cart));
          // console.log(`Fixed cart store IDs to use ${DEFAULT_STORE_ID}`);
        }
      }
    } catch (error) {
      console.error("Error fixing cart store IDs:", error);
    }
  }, []);
  useEffect(() => {
    // Only run this fix if we haven't initialized yet
    if (!isInitialized) {
      fixCartStoreIds();
    }
  }, [fixCartStoreIds, isInitialized]);
  // OPTIMIZED: Fetch cart when user changes - TRUST CACHE FIRST
  useEffect(() => {
    // If we already initialized this session, don't run again unless user changes
    if (isInitialized && !currentUser) return;

    const fetchUserCart = async () => {
      const cacheKey = currentUser ? `userCart_${currentUser.uid}` : 'guestCart';
      const cachedCart = sessionStorage.getItem(cacheKey);
      const cacheTimestamp = sessionStorage.getItem(`${cacheKey}_timestamp`);

      // 1. Check Cache Validity - INCREASED CACHE LIFE to 5 minutes
      if (cachedCart && cacheTimestamp) {
        const age = Date.now() - parseInt(cacheTimestamp);
        if (age < 5 * 60 * 1000) { // 5 minutes instead of 2
          const parsedCart = JSON.parse(cachedCart);
          const filteredCart = filterCartByStore(parsedCart);
          setCart(filteredCart);
          setLoading(false);
          setIsInitialized(true);
          return; // 🛑 STOP HERE. Do not fetch from DB.
        }
      }

      // 2. Only fetch if cache is stale or missing
      setLoading(true);

      try {
        if (currentUser && localStorage.getItem('guestCart')) {
          // Migration Logic (Expensive but necessary once)
          const migratedCart = await migrateGuestCart(currentUser.uid);
          const filteredCart = filterCartByStore(migratedCart);
          setCart(filteredCart);

          // Update Cache
          sessionStorage.setItem(cacheKey, JSON.stringify(migratedCart));
          sessionStorage.setItem(`${cacheKey}_timestamp`, Date.now().toString());

          // Remove guest cart after successful migration
          localStorage.removeItem('guestCart');
        } else {
          // Standard Fetch
          const cartData = await getCart(currentUser);
          const filteredCart = filterCartByStore(cartData);
          setCart(filteredCart);

          // Update Cache
          sessionStorage.setItem(cacheKey, JSON.stringify(cartData));
          sessionStorage.setItem(`${cacheKey}_timestamp`, Date.now().toString());
        }
      } catch (error) {
        console.error("Cart fetch error:", error);
      } finally {
        setLoading(false);
        setIsInitialized(true);
      }
    };

    fetchUserCart();
  }, [currentUser]); // Removed internal dependency loops

  // Update item quantity - with immediate UI update
  const handleUpdateQuantity = async (productId, quantity, variantId = null) => {
    try {
      // Find the item to update
      const itemIndex = cart.items.findIndex(item =>
        item.productId === productId &&
        (variantId ? item.variantId === variantId : !item.variantId) &&
        item.storeId === DEFAULT_STORE_ID
      );

      if (itemIndex === -1) return false;

      // Clone cart items
      const updatedItems = [...cart.items];
      const oldQuantity = updatedItems[itemIndex].quantity;
      const priceDifference = updatedItems[itemIndex].price * (quantity - oldQuantity);

      // Update quantity
      updatedItems[itemIndex] = {
        ...updatedItems[itemIndex],
        quantity: quantity
      };

      // Update UI immediately
      setCart({
        items: updatedItems,
        subtotal: cart.subtotal + priceDifference
      });

      // Then update backend
      const updatedCart = await updateCartItemQuantity(productId, quantity, variantId, currentUser);
      const filteredCart = filterCartByStore(updatedCart);

      // Update with confirmed data from backend
      setCart(filteredCart);
      return true;
    } catch (error) {
      console.error("Error updating quantity:", error);

      // Revert to original cart on error
      const originalCart = await getCart(currentUser);
      const filteredCart = filterCartByStore(originalCart);
      setCart(filteredCart);
      return false;
    }
  };

  // Remove item - with immediate UI update
  const handleRemoveItem = async (productId, variantId = null) => {
    try {
      // Find the item to remove
      const itemToRemove = cart.items.find(item =>
        item.productId === productId &&
        (variantId ? item.variantId === variantId : !item.variantId) &&
        item.storeId === DEFAULT_STORE_ID
      );

      if (!itemToRemove) return false;

      // Calculate new subtotal
      const newSubtotal = cart.subtotal - (itemToRemove.price * itemToRemove.quantity);

      // Filter out the removed item
      const updatedItems = cart.items.filter(item =>
        !(item.productId === productId && (variantId ? item.variantId === variantId : !item.variantId))
      );

      // Update UI immediately
      setCart({
        items: updatedItems,
        subtotal: newSubtotal
      });

      // Then update backend
      const updatedCart = await removeCartItem(productId, variantId, currentUser);
      const filteredCart = filterCartByStore(updatedCart);

      // Update with confirmed data from backend
      setCart(filteredCart);
      return true;
    } catch (error) {
      console.error("Error removing item:", error);

      // Revert to original cart on error
      const originalCart = await getCart(currentUser);
      const filteredCart = filterCartByStore(originalCart);
      setCart(filteredCart);
      return false;
    }
  };

  // Clear cart
  const handleClearCart = async () => {
    try {
      // Update UI immediately
      setCart({ items: [], subtotal: 0 });

      // Then update backend
      const emptyCart = await clearCart(currentUser);
      const filteredCart = filterCartByStore(emptyCart);

      // Update with confirmed data from backend
      setCart(filteredCart);
      return true;
    } catch (error) {
      console.error("Error clearing cart:", error);

      // Revert to original cart on error
      const originalCart = await getCart(currentUser);
      const filteredCart = filterCartByStore(originalCart);
      setCart(filteredCart);
      return false;
    }
  };

  // Toggle cart panel - update to handle forced states
  const toggleCart = (forceState = null) => {
    // Set a slight delay before closing to allow animations
    if (forceState === false) {
      setTimeout(() => {
        setCartOpen(false);
      }, 10);
    } else {
      setCartOpen(prev => forceState !== null ? forceState : !prev);
    }
  };

  // Get cart item count
  const getItemCount = () => {
    return cart.items.reduce((count, item) => count + item.quantity, 0);
  };

  // Format currency using centralized system
  const formatCurrency = useCallback((amount) => {
    const currencyCode = storeSettings?.currency || 'AED';
    return formatCurrencyDisplay(amount, currencyCode, language);
  }, [storeSettings, language]);

  // Legacy function that returns just the number (for backward compatibility)
  const formatCurrencyNumber = (amount) => {
    return parseFloat(amount || 0).toFixed(2);
  };

  // Add item to cart - with immediate UI update
  const handleAddToCart = async (product, quantity = 1, variant = null) => {
    // console.time('CartContext.addToCart'); // 🔍 Performance timing

    try {
      // Check if the product is already in the cart with the same variant
      const existingItemIndex = cart.items.findIndex(item =>
        item.productId === product.id &&
        (variant ? item.variantId === variant.id : !item.variantId) &&
        item.storeId === DEFAULT_STORE_ID
      );

      let updatedItems = [...cart.items];
      let newSubtotal = cart.subtotal;

      // console.log('🟡 Preparing cart update...'); // 🔍 Debug log

      if (existingItemIndex >= 0) {
        // Update existing item quantity and multilingual data
        const newQuantity = updatedItems[existingItemIndex].quantity + quantity;
        updatedItems[existingItemIndex] = {
          ...updatedItems[existingItemIndex],
          quantity: newQuantity,
          name: product.name, // Update with current multilingual name
          ...(variant && {
            color: variant.color,    // Update multilingual color
            size: variant.size,      // Update multilingual size  
            material: variant.material, // Update multilingual material
            style: variant.style,    // Update multilingual style
            displayVariant: generateDisplayVariant(product, variant) // Update display variant
          })
        };
        newSubtotal += product.price * quantity;
      } else {
        // Create the new item - store multilingual data
        const newItem = {
          productId: product.id,
          name: product.name, // Store raw multilingual name object
          price: product.price,
          quantity: quantity,
          image: product.imageURL || product.images?.[0] || null,
          storeId: DEFAULT_STORE_ID, // Ensure storeId is always set
          ...(variant && {
            variantId: variant.id,
            color: variant.color,    // Store multilingual color object
            size: variant.size,      // Store multilingual size object
            material: variant.material, // Store multilingual material object
            style: variant.style,    // Store multilingual style object
            displayVariant: generateDisplayVariant(product, variant) // Generate display variant
          })
        };

        updatedItems.push(newItem);
        newSubtotal += product.price * quantity;
      }

      // console.log('🟢 Updating UI immediately...'); // 🔍 Debug log

      // IMMEDIATELY update UI
      setCart({
        items: updatedItems,
        subtotal: newSubtotal
      });

      // Open cart immediately for instant feedback
      setCartOpen(true);

      // console.log('🟢 UI updated, starting background sync...'); // 🔍 Debug log

      // Then update backend in the background (NON-BLOCKING)
      addToCart(product, quantity, variant, currentUser, DEFAULT_STORE_ID).catch(error => {
        // console.error("Background cart update failed:", error);
        // Could show a toast notification here if needed
      });

      // console.timeEnd('CartContext.addToCart'); // 🔍 Performance timing
      return true;
    } catch (error) {
      console.error("Error adding to cart:", error);
      console.timeEnd('CartContext.addToCart'); // 🔍 Performance timing

      // Revert to original cart on error
      const originalCart = await getCart(currentUser);
      const filteredCart = filterCartByStore(originalCart);
      setCart(filteredCart);
      return false;
    }
  };

  // Update cart with latest data
  const refreshCart = useCallback(async (enrichData = false) => {
    try {
      const updatedCart = await getCart(currentUser, enrichData);
      const filteredCart = filterCartByStore(updatedCart);
      setCart(filteredCart);
    } catch (error) {
      console.error("Error refreshing cart:", error);
    }
  }, [currentUser]);

  // Get cart with fresh pricing data (for checkout)
  const getCartWithFreshData = useCallback(async () => {
    try {
      const updatedCart = await getCart(currentUser, true); // Enable enrichment
      return filterCartByStore(updatedCart);
    } catch (error) {
      console.error("Error getting cart with fresh data:", error);
      return cart; // Return current cart as fallback
    }
  }, [currentUser, cart]);

  const value = {
    cart,
    loading,
    cartOpen,
    cartVisible,         // ✅ add this
    setCartVisible,      // ✅ add this
    toggleCart,
    openCart,
    closeCart,
    addToCart: handleAddToCart,
    updateQuantity: handleUpdateQuantity,
    removeItem: handleRemoveItem,
    clearCart: handleClearCart,
    refreshCart,
    getCartWithFreshData, // ✅ For checkout and validation
    itemCount: getItemCount(),
    formatCurrency,
    formatCurrencyNumber  // Add this for components that need just numbers
  };



  return (
    <CartContext.Provider value={value}>
      {children}
    </CartContext.Provider>
  );
};

export const useCart = () => {
  const context = useContext(CartContext);
  if (context === undefined) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
};

export default CartContext;